/**
 * Admin gallery page — folders, item upload, share-link create + revoke.
 *
 * Route: /admin/gallery
 *
 * Scope picker mirrors the InvitationsPage / PageEditor pattern. Folder
 * tree (flat list with path strings), upload to a chosen folder, list of
 * existing share links with revoke buttons, and a "Create share link"
 * form.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ApiClientError,
  chaptersApi,
  galleriesApi,
  type BranchSummary,
  type ChapterSummary,
  type GalleryFolder,
  type GalleryItem,
  type GalleryShareLink,
  type ScopeType,
} from '@/services/api';

export function GalleryAdminPage() {
  // Scope picker
  const [chapters, setChapters] = useState<ChapterSummary[]>([]);
  const [branches, setBranches] = useState<BranchSummary[]>([]);
  const [scopeType, setScopeType] = useState<ScopeType>('chapter');
  const [chapterId, setChapterId] = useState('');
  const [branchId, setBranchId] = useState('');

  // Gallery state
  const [folders, setFolders] = useState<GalleryFolder[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedFolderId, setSelectedFolderId] = useState<string | ''>('');
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [galleryError, setGalleryError] = useState<string | null>(null);

  // Create folder
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderParent, setNewFolderParent] = useState<string | ''>('');

  // Upload
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploadTags, setUploadTags] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Pending approvals
  const [pending, setPending] = useState<GalleryItem[]>([]);
  const [pendingBusyId, setPendingBusyId] = useState<string | null>(null);
  const [pendingError, setPendingError] = useState<string | null>(null);

  // Share links
  const [shareLinks, setShareLinks] = useState<GalleryShareLink[]>([]);
  const [shareExpiresHours, setShareExpiresHours] = useState(24);
  const [shareFilterFolderId, setShareFilterFolderId] = useState<string | ''>('');
  const [shareMaxUses, setShareMaxUses] = useState<string>('');
  const [shareError, setShareError] = useState<string | null>(null);

  const selectedScopeId = useMemo(() => (scopeType === 'branch' ? branchId : chapterId), [scopeType, chapterId, branchId]);

  // Load chapters
  useEffect(() => {
    chaptersApi.list('navarasam').then((rows) => {
      setChapters(rows);
      if (rows.length > 0 && !chapterId) setChapterId(rows[0]!.id);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load branches when chapter changes
  useEffect(() => {
    if (!chapterId) return;
    chaptersApi.listBranches(chapterId).then((rows) => {
      setBranches(rows);
      if (scopeType === 'branch' && rows.length > 0 && !branchId) setBranchId(rows[0]!.id);
    });
  }, [chapterId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load gallery + folders for scope
  const loadGallery = useCallback(async () => {
    if (!selectedScopeId) return;
    try {
      const data = await galleriesApi.get(scopeType, selectedScopeId);
      setFolders(data.folders);
      setTotalItems(data.totalItems);
      setGalleryError(null);
    } catch (err) {
      setGalleryError(err instanceof Error ? err.message : 'Failed to load gallery');
    }
  }, [scopeType, selectedScopeId]);

  const loadItems = useCallback(async () => {
    if (!selectedScopeId) return;
    try {
      const data = await galleriesApi.listItems(scopeType, selectedScopeId, {
        folderId: selectedFolderId || undefined,
        pageSize: 50,
      });
      setItems(data.items);
    } catch {
      setItems([]);
    }
  }, [scopeType, selectedScopeId, selectedFolderId]);

  const loadShareLinks = useCallback(async () => {
    if (!selectedScopeId) return;
    try {
      const links = await galleriesApi.listShareLinks(scopeType, selectedScopeId);
      setShareLinks(links);
    } catch {
      setShareLinks([]);
    }
  }, [scopeType, selectedScopeId]);

  const loadPending = useCallback(async () => {
    if (!selectedScopeId) return;
    setPendingError(null);
    try {
      const data = await galleriesApi.listItems(scopeType, selectedScopeId, {
        status: 'pending',
        pageSize: 100,
      });
      setPending(data.items);
    } catch (err) {
      setPending([]);
      setPendingError(err instanceof Error ? err.message : 'Failed to load pending items');
    }
  }, [scopeType, selectedScopeId]);

  async function moderate(itemId: string, status: 'approved' | 'rejected') {
    if (!selectedScopeId) return;
    setPendingBusyId(itemId);
    setPendingError(null);
    try {
      await galleriesApi.moderateItem(scopeType, selectedScopeId, itemId, status);
      await Promise.all([loadPending(), loadItems(), loadGallery()]);
    } catch (err) {
      setPendingError(err instanceof Error ? err.message : 'Moderation failed');
    } finally {
      setPendingBusyId(null);
    }
  }

  useEffect(() => {
    void loadGallery();
    void loadShareLinks();
    void loadPending();
  }, [loadGallery, loadShareLinks, loadPending]);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  // ---------- Mutations ----------

  async function handleCreateFolder() {
    if (!newFolderName.trim()) return;
    try {
      await galleriesApi.createFolder(scopeType, selectedScopeId, newFolderName.trim(), newFolderParent || undefined);
      setNewFolderName('');
      setNewFolderParent('');
      await loadGallery();
    } catch (err) {
      setGalleryError(err instanceof Error ? err.message : 'Failed to create folder');
    }
  }

  async function handleDeleteFolder(id: string) {
    if (!confirm('Delete this folder? (Only works if empty.)')) return;
    try {
      await galleriesApi.deleteFolder(scopeType, selectedScopeId, id);
      await loadGallery();
    } catch (err) {
      setGalleryError(err instanceof Error ? err.message : 'Failed to delete folder');
    }
  }

  async function handleUpload() {
    if (uploadFiles.length === 0) {
      setUploadError('Select at least one file');
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const tags = uploadTags.split(',').map((s) => s.trim()).filter(Boolean);
      await galleriesApi.uploadItems(scopeType, selectedScopeId, uploadFiles, {
        folderId: selectedFolderId || undefined,
        tags: tags.length > 0 ? tags : undefined,
      });
      setUploadFiles([]);
      setUploadTags('');
      await loadGallery();
      await loadItems();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function handleCreateShareLink() {
    setShareError(null);
    const expiresAt = new Date(Date.now() + shareExpiresHours * 3600 * 1000).toISOString();
    try {
      await galleriesApi.createShareLink(scopeType, selectedScopeId, {
        filter: shareFilterFolderId ? { folderId: shareFilterFolderId } : undefined,
        expiresAt,
        maxUses: shareMaxUses ? Number(shareMaxUses) : undefined,
      });
      await loadShareLinks();
    } catch (err) {
      if (err instanceof ApiClientError) setShareError(err.message);
      else setShareError('Failed to create share link');
    }
  }

  async function handleRevokeShareLink(id: string) {
    try {
      await galleriesApi.revokeShareLink(id);
      await loadShareLinks();
    } catch (err) {
      setShareError(err instanceof Error ? err.message : 'Failed to revoke');
    }
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-display font-bold text-white">Gallery</h1>
        <p className="text-white/60 mt-1">Manage folders, upload photos and videos, generate share links.</p>
      </div>

      {/* Scope picker */}
      <section className="card">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Scope</label>
            <select value={scopeType} onChange={(e) => setScopeType(e.target.value as ScopeType)} className="input">
              <option value="chapter">Chapter</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Chapter</label>
            <select value={chapterId} onChange={(e) => setChapterId(e.target.value)} className="input">
              {chapters.map((c) => (
                <option key={c.id} value={c.id}>{c.displayName}</option>
              ))}
            </select>
          </div>
          {scopeType === 'branch' && (
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">Branch</label>
              <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className="input" disabled={branches.length === 0}>
                {branches.length === 0 ? (
                  <option value="">— No branches —</option>
                ) : (
                  branches.map((b) => <option key={b.id} value={b.id}>{b.displayName ?? b.name}</option>)
                )}
              </select>
            </div>
          )}
        </div>
      </section>

      {/* Folders */}
      <section className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-white">Folders</h2>
          <span className="text-xs text-white/40">{totalItems} item{totalItems === 1 ? '' : 's'} in this gallery</span>
        </div>
        {galleryError && <p className="text-red-400 mb-3">{galleryError}</p>}
        <div className="space-y-2 mb-4">
          <div
            onClick={() => setSelectedFolderId('')}
            className={`px-2 py-1 rounded cursor-pointer ${selectedFolderId === '' ? 'bg-primary/10 text-white' : 'text-white/80 hover:bg-white/5'}`}
          >
            (root / all)
          </div>
          {folders.map((f) => (
            <div
              key={f.id}
              className={`px-2 py-1 rounded flex justify-between items-center cursor-pointer ${selectedFolderId === f.id ? 'bg-primary/10 text-white' : 'text-white/80 hover:bg-white/5'}`}
              onClick={() => setSelectedFolderId(f.id)}
            >
              <span className="font-mono text-sm">{f.path}</span>
              <button type="button" onClick={(e) => { e.stopPropagation(); void handleDeleteFolder(f.id); }} className="text-xs text-red-400 hover:text-red-300">Delete</button>
            </div>
          ))}
        </div>
        <div className="grid md:grid-cols-3 gap-3 items-end pt-3 border-t border-white/10">
          <div>
            <label className="block text-xs text-white/60 mb-1">New folder name</label>
            <input value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} className="input" placeholder="Reunion" />
          </div>
          <div>
            <label className="block text-xs text-white/60 mb-1">Parent (optional)</label>
            <select value={newFolderParent} onChange={(e) => setNewFolderParent(e.target.value)} className="input">
              <option value="">(root)</option>
              {folders.map((f) => <option key={f.id} value={f.id}>{f.path}</option>)}
            </select>
          </div>
          <button type="button" onClick={handleCreateFolder} className="btn-secondary text-sm">Create folder</button>
        </div>
      </section>

      {/* Upload */}
      <section className="card">
        <h2 className="text-xl font-semibold text-white mb-4">Upload to {selectedFolderId ? folders.find((f) => f.id === selectedFolderId)?.path : 'root'}</h2>
        <div className="space-y-3">
          <input
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={(e) => setUploadFiles(Array.from(e.target.files ?? []))}
            className="text-sm text-white/80"
          />
          <div>
            <label className="block text-xs text-white/60 mb-1">Tags (comma-separated)</label>
            <input value={uploadTags} onChange={(e) => setUploadTags(e.target.value)} className="input" placeholder="reunion, group" />
          </div>
          {uploadError && <p className="text-red-400 text-sm">{uploadError}</p>}
          <button type="button" onClick={handleUpload} disabled={uploading} className="btn-primary">
            {uploading ? 'Uploading…' : `Upload ${uploadFiles.length || ''} file${uploadFiles.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </section>

      {/* Items preview */}
      {/* Pending approvals — member uploads waiting on a chapter admin. */}
      <section className="card">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">
            Pending approvals {pending.length > 0 && <span className="text-sm text-yellow-400 ml-2">({pending.length})</span>}
          </h2>
          {pendingError && <span className="text-sm text-red-400">{pendingError}</span>}
        </div>
        {pending.length === 0 ? (
          <p className="text-white/60 text-sm">Nothing awaiting approval. Photos uploaded by members appear here until you approve or reject.</p>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {pending.map((it) => (
              <li key={it.id} className="bg-white/5 rounded overflow-hidden flex flex-col">
                {it.mediaKind === 'video' ? (
                  <div className="aspect-video bg-black/40 flex items-center justify-center text-xs text-white/60">video</div>
                ) : (
                  <img
                    src={it.thumbnailUrl ?? it.blobUrl}
                    alt={it.caption ?? ''}
                    className="w-full aspect-square object-cover"
                    loading="lazy"
                  />
                )}
                <div className="p-2 flex-1 flex flex-col gap-2">
                  {it.caption && <p className="text-xs text-white/70 line-clamp-2">{it.caption}</p>}
                  <p className="text-[10px] text-white/40">Uploaded {new Date(it.createdAt).toLocaleString()}</p>
                  <div className="mt-auto flex gap-2">
                    <button
                      type="button"
                      disabled={pendingBusyId === it.id}
                      onClick={() => moderate(it.id, 'approved')}
                      className="text-xs px-3 py-1 bg-green-500/20 text-green-300 border border-green-500/30 rounded hover:bg-green-500/30 disabled:opacity-40"
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      disabled={pendingBusyId === it.id}
                      onClick={() => moderate(it.id, 'rejected')}
                      className="text-xs px-3 py-1 bg-red-500/20 text-red-300 border border-red-500/30 rounded hover:bg-red-500/30 disabled:opacity-40"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card">
        <h2 className="text-xl font-semibold text-white mb-4">Items</h2>
        {items.length === 0 ? (
          <p className="text-white/60">No items in this folder.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {items.map((it) => (
              <figure key={it.id} className="bg-white/5 rounded overflow-hidden">
                {it.mediaKind === 'video' ? (
                  <div className="aspect-square bg-black/40 flex items-center justify-center text-xs text-white/60">video</div>
                ) : (
                  <img src={it.thumbnailUrl ?? it.blobUrl} alt={it.caption ?? ''} className="w-full aspect-square object-cover" />
                )}
              </figure>
            ))}
          </div>
        )}
      </section>

      {/* Share links */}
      <section className="card">
        <h2 className="text-xl font-semibold text-white mb-4">Share links</h2>
        <div className="grid md:grid-cols-4 gap-3 items-end mb-4">
          <div>
            <label className="block text-xs text-white/60 mb-1">Filter folder (optional)</label>
            <select value={shareFilterFolderId} onChange={(e) => setShareFilterFolderId(e.target.value)} className="input">
              <option value="">(no folder filter)</option>
              {folders.map((f) => <option key={f.id} value={f.id}>{f.path}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-white/60 mb-1">Expires in (hours)</label>
            <input type="number" min={1} max={720} value={shareExpiresHours} onChange={(e) => setShareExpiresHours(Math.max(1, Number(e.target.value || 24)))} className="input" />
          </div>
          <div>
            <label className="block text-xs text-white/60 mb-1">Max uses (optional)</label>
            <input type="number" min={1} value={shareMaxUses} onChange={(e) => setShareMaxUses(e.target.value)} className="input" placeholder="unlimited" />
          </div>
          <button type="button" onClick={handleCreateShareLink} className="btn-primary">Create link</button>
        </div>
        {shareError && <p className="text-red-400 text-sm mb-3">{shareError}</p>}
        {shareLinks.length === 0 ? (
          <p className="text-white/60">No share links yet.</p>
        ) : (
          <ul className="divide-y divide-white/10">
            {shareLinks.map((link) => {
              const fullUrl = `${window.location.origin}${link.url}`;
              return (
                <li key={link.id} className="py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <button
                      type="button"
                      onClick={() => void navigator.clipboard.writeText(fullUrl)}
                      className="font-mono text-xs text-white/70 hover:text-white truncate block w-full text-left"
                      title={fullUrl}
                    >
                      {fullUrl}
                    </button>
                    <p className="text-xs text-white/40">
                      Expires {new Date(link.expiresAt).toLocaleString()} · used {link.usedCount}{link.maxUses ? ` / ${link.maxUses}` : ''}
                      {link.filter?.folderId && ' · folder filter'}
                    </p>
                  </div>
                  <span className={`text-xs ${link.isActive ? 'text-green-400' : 'text-white/40'}`}>{link.isActive ? 'Active' : 'Inactive'}</span>
                  {link.isActive && (
                    <button type="button" onClick={() => handleRevokeShareLink(link.id)} className="text-xs text-red-400 hover:text-red-300">Revoke</button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

export default GalleryAdminPage;
